import type { ReactNode } from 'react';

import { Badge } from '@lib/ui';
import { Flex } from '@lib/ui';
import type { ColorType } from '@lib/types';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';
import { useSidebarMenu } from './SidebarMenu.context';

const sidebarNavItemVariants = cva(
  [
    // Layout
    'group min-w-0 flex-1 rounded-md px-3 py-2',
    // Typography
    'font-medium',
    // Cursor
    'cursor-pointer',
    // Transitions
    'outline-hidden transition-colors ease-in-out',
  ],
  {
    variants: {
      isActive: {
        true: 'text-this-solid',
        false: [
          'text-fg-muted',
          // Hover
          'hover:bg-ui-hover hover:text-fg',
          // Focus
          'focus:bg-ui-hover focus:text-fg',
        ],
      },
    },
    defaultVariants: {
      isActive: false,
    },
  }
);

const iconVariants = cva('shrink-0 [&>svg]:size-5 [&>svg]:stroke-[1.5]', {
  variants: {
    isActive: {
      true: '',
      false: 'opacity-80 group-hover:opacity-100',
    },
  },
  defaultVariants: {
    isActive: false,
  },
});

const dotVariants = cva('size-2 shrink-0 rounded-full border border-current', {
  variants: {
    isActive: {
      true: 'bg-this-solid opacity-80',
      false: 'opacity-50 transition-all group-hover:opacity-100',
    },
  },
  defaultVariants: {
    isActive: false,
  },
});

export interface SidebarNavItemProps extends VariantProps<typeof sidebarNavItemVariants> {
  /** Unique identifier */
  id: string;
  /** Icon element (not used when variant is "nested") */
  icon?: ReactNode;
  /** Menu item label (children) */
  children: ReactNode;
  /** URL path for navigation */
  href?: string;
  /** Called when item is clicked */
  onClick?: () => void;
  /** Whether this item is currently active */
  isActive?: boolean;
  /** Badge to display (e.g., notification count) */
  badge?: string | number;
  /** Badge color */
  badgeColor?: ColorType;
  /** Variant: "default" shows icon, "nested" shows dot indicator for child items in groups */
  variant?: 'default' | 'nested';
}

export function SidebarNavItem({
  id,
  icon,
  children,
  href,
  onClick,
  isActive: isActiveProp,
  badge,
  badgeColor,
  variant = 'default',
}: SidebarNavItemProps) {
  const {
    linkComponent: LinkComponent,
    onSelect,
    activeId,
    onMobileClose,
    isMobile,
  } = useSidebarMenu();

  const isActive = isActiveProp ?? id === activeId;
  const Element = href ? LinkComponent : 'button';
  const isNested = variant === 'nested';

  const handleClick = () => {
    onClick?.();
    onSelect?.(id);
    // Auto-close sidebar on mobile
    if (isMobile) {
      onMobileClose?.();
    }
  };

  return (
    <Flex className={cn('relative', isNested ? '' : 'px-3')} data-sidebar-active={isActive}>
      <Element
        href={href}
        to={href}
        onClick={handleClick}
        className={cn(setThisClass('primary'), sidebarNavItemVariants({ isActive }))}
      >
        <Flex
          align="center"
          justify="between"
          gap="2"
          data-menu-active={isActive}
          className="min-w-0 text-xs-plus tracking-wide"
        >
          <Flex align="center" gap="3" className="min-w-0">
            {isNested ? (
              <div className={dotVariants({ isActive })} />
            ) : (
              <span className={iconVariants({ isActive })}>{icon}</span>
            )}
            <span className="truncate">{children}</span>
          </Flex>
          {badge !== undefined && (
            <Badge variant="soft" size="sm" color={badgeColor ?? 'primary'} className="shrink-0">
              {badge}
            </Badge>
          )}
        </Flex>
        {/* Active indicator bar - only for default variant */}
        {isActive && !isNested && (
          <div className="absolute inset-y-1 start-0 w-1 rounded-e-full bg-this-solid" />
        )}
      </Element>
    </Flex>
  );
}

SidebarNavItem.displayName = 'SidebarNavItem';
